import React from 'react'
import ClaimDamageForm from './components/ClaimDamageForm'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import {
  useAppDispatch,
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import { getClaimMethod } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import ClaimDamageExternal from './components/ClaimDamageExternal'
import {
  getCurrentStep,
  getIsFormCompleted
} from '@verzekeringapp-mono/shared/src/state/selectors/claims'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  IClaimsState,
  setCurrentStep
} from '@verzekeringapp-mono/shared/src/state/slices/claims_slice'
import ClaimDamageOverview from './components/ClaimDamageOverview'
import ClaimDamageConfirmation from './components/ClaimDamageConfirmation'
import Modal from '~/components/Modal'
import LoadingIndicator from '~/components/LoadingIndicator'
import ClaimDamageUnclaimable from './components/ClaimDamageUnclaimable'

const ClaimDamageFormScreen = () => {
  const claimMethod = useAppSelector(getClaimMethod)
  const currentStep = useAppSelector(getCurrentStep)
  const isFormCompleted = useAppSelector(getIsFormCompleted)
  const isLoading = useAppSelector(state => state.products.isLoading)
  const dispatch = useAppDispatch()

  const renderLoadingModal = () => (
    <Modal>
      <div className='flex justify-center items-center'>
        <LoadingIndicator />
      </div>
    </Modal>
  )

  const ClaimDamageStepsHeader = () => (
    <div className='text-center'>
      <div className='hidden md:block'>
        <h1 className='mb-4 font-montserrat text-4xl font-medium text-text-primary'>
          Schade melden
        </h1>

        <p className='mb-10 text-text-secondary'>
          Doorloop het formulier om de opgelopen schade te melden.
        </p>

        <div className='flex justify-between w-3/6 m-auto md:my-8'>
          {renderStepButton(1, 'Algemeen')}

          {renderStepButton(2, 'Overzicht')}
        </div>
      </div>
    </div>
  )

  const renderStepButton = (step: number, label: string) => (
    <div className='flex flex-col items-center'>
      <button
        className={`flex h-12 w-12 items-center justify-center rounded-full border text-2xl ${
          step < currentStep && isFormCompleted
            ? 'border-status-success-strong bg-status-success-strong text-status-success-on-strong'
            : step > currentStep && !isFormCompleted
              ? 'cursor-not-allowed border-border-default bg-surface-card-disabled text-text-secondary'
              : 'border-action-primary-border bg-action-primary-background text-action-primary-foreground'
        }`}
        onClick={() => handleStepClick(step)}
        disabled={step > currentStep || (step === 3 && currentStep === 3)}
        type='button'
      >
        {step < currentStep && isFormCompleted
          ? (
            <FontAwesomeIcon icon='check' />
          )
          : (
            step
          )}
      </button>

      <p className={`mt-4 text-lg ${step > currentStep && !isFormCompleted ? 'text-text-secondary' : 'text-text-primary'}`}>{label}</p>
    </div>
  )

  const handleStepClick = (step: number) => {
    if (step <= currentStep || isFormCompleted) {
      dispatch(setCurrentStep(step))
    }
  }

  return (
    <ScreenWrapper hasPadding={false}>
      {isLoading &&
        (
          renderLoadingModal()
        )
      }

      {!isLoading && (claimMethod.externalLink || claimMethod.claimLink) &&
        (
          <ClaimDamageExternal claimMethod={claimMethod} />
        )
      }

      {
        !isLoading && !claimMethod.isClaimable && !(claimMethod.externalLink || claimMethod.claimLink) &&
        (
          <ClaimDamageUnclaimable claimMethod={claimMethod} />
        )
      }

      {!claimMethod.externalLink && claimMethod.isClaimable && (
        <>
          <ClaimDamageStepsHeader />

          <ClaimDamageSteps currentStep={currentStep} />
        </>
      )}
    </ScreenWrapper>
  )
}

const ClaimDamageSteps = ({
  currentStep
}: {
  currentStep: IClaimsState['currentStep']
}) => {
  const steps = {
    1: <ClaimDamageForm />,
    2: <ClaimDamageOverview />,
    3: <ClaimDamageConfirmation />
  }

  return steps[currentStep as keyof typeof steps] || <ClaimDamageForm />
}

export default ClaimDamageFormScreen
